/**
 * XSS sanitization utilities using DOMPurify.
 * Use sanitizeRichHtml() for content that is rendered with v-html.
 * Use sanitizePlainText() for user input (names, emails, comments) before validation and submit.
 */

import DOMPurify from 'dompurify'
import he from 'he'

/**
 * Strip all HTML and return plain text. Use for names, emails, comments, etc.
 *
 * @param {string} dirty - Raw string (may contain HTML/scripts)
 * @returns {string} Plain text safe to send and display
 */
/**
 * Decode HTML entities (e.g. &amp; → &) for plain-text editing fields.
 *
 * @param {string} dirty - Raw string from API/storage
 * @returns {string} Decoded plain text
 */
export function decodeHtmlEntities (dirty) {
  if (dirty == null || typeof dirty !== 'string') {
    return ''
  }
  return he.decode(dirty)
}

/**
 * Decode HTML entities in each locale string (e.g. { nl: 'A &amp; B' }).
 *
 * @param {Object} obj - Object with locale keys and string values
 * @returns {Object} New object with decoded values
 */
export function decodeLocaleObject (obj) {
  if (obj == null || typeof obj !== 'object') {
    return {}
  }
  return Object.keys(obj).reduce((out, key) => {
    const val = obj[key]
    if (typeof val === 'string') {
      out[key] = decodeHtmlEntities(val)
    }
    return out
  }, {})
}

export function sanitizePlainText (dirty) {
  if (dirty == null || typeof dirty !== 'string') {
    return ''
  }
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [] }).trim()
}

/**
 * Sanitize HTML for rich content.
 * Allows more tags for formatted text while still blocking scripts.
 *
 * @param {string} dirty - Raw HTML string
 * @returns {string} Sanitized HTML safe to render
 */
export function sanitizeRichHtml (dirty) {
  if (dirty == null || typeof dirty !== 'string') {
    return ''
  }
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      'a', 'b', 'br', 'em', 'h1', 'h2', 'i', 'li', 'ol', 'p', 'strong', 'u', 'ul'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
  })
}

/**
 * Basic email format validation for user input.
 *
 * @param {string} email
 * @returns {boolean}
 */
export function isValidEmail (email) {
  if (email == null || typeof email !== 'string') {
    return false
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
}
